import type { Coord, GameState, Unit } from "@dailywars/core/engine";
import type { ReactNode } from "react";

import { BoardActionStatusBar } from "#/components/game/BoardActionStatusBar";
import { EndTurnButton } from "#/components/game/EndTurnButton";
import { GameSettingsMenu } from "#/components/game/GameSettingsMenu";
import { TileInspectorBar } from "#/components/game/TileInspectorBar";
import { UndoButton } from "#/components/game/UndoButton";
import { useCanEndTurn, useCanUndo, useInspectedUnit } from "#/game/gameSelectors";
import { useGameSelector } from "#/game/GameStoreProvider";

export function PlayBottomDockBar({
  status,
  localState,
  inspectedTile,
  inspectedUnit,
  handleEndTurn,
  canEndTurn,
  handleUndo,
  canUndo,
  isSubmitting,
  handleRestart,
  isRestarting,
  actions,
}: {
  status: GameState["status"];
  localState: GameState;
  inspectedTile: Coord | null;
  inspectedUnit: Unit | null;
  handleEndTurn: () => void | Promise<void>;
  canEndTurn: boolean;
  handleUndo: () => void;
  canUndo: boolean;
  isSubmitting: boolean;
  handleRestart: () => void | Promise<void>;
  isRestarting: boolean;
  actions?: ReactNode;
}) {
  const defaultActions =
    status === "in_progress" ? (
      <>
        <UndoButton onClick={handleUndo} disabled={!canUndo} />
        <EndTurnButton onClick={handleEndTurn} disabled={!canEndTurn} pending={isSubmitting} />
        <GameSettingsMenu onRestart={handleRestart} isRestarting={isRestarting} />
      </>
    ) : undefined;

  return (
    <div className="shrink-0">
      {status === "in_progress" ? <BoardActionStatusBar /> : null}
      <TileInspectorBar
        state={localState}
        tile={inspectedTile}
        unit={inspectedUnit}
        actions={actions ?? defaultActions}
      />
    </div>
  );
}

type PlayMapDockBarsProps = {
  handleEndTurn: () => void | Promise<void>;
  handleRestart: () => void | Promise<void>;
  isRestarting: boolean;
};

export function PlayMapDockBars({
  handleEndTurn,
  handleRestart,
  isRestarting,
}: PlayMapDockBarsProps) {
  const localState = useGameSelector((s) => s.localState);
  const inspectedTile = useGameSelector((s) => s.hoveredTile);
  const inspectedUnit = useInspectedUnit();
  const isSubmitting = useGameSelector((s) => s.isSubmitting);
  const canEndTurn = useCanEndTurn();
  const canUndo = useCanUndo();
  const undoLastMove = useGameSelector((s) => s.undoLastMove);

  return (
    <PlayBottomDockBar
      status={localState.status}
      localState={localState}
      inspectedTile={inspectedTile}
      inspectedUnit={inspectedUnit}
      handleEndTurn={handleEndTurn}
      canEndTurn={canEndTurn}
      handleUndo={undoLastMove}
      canUndo={canUndo}
      isSubmitting={isSubmitting}
      handleRestart={handleRestart}
      isRestarting={isRestarting}
    />
  );
}
